/**
 * Section-based navigation model — workflow-first restructure.
 *
 * The sidebar is reduced to 5 sections: Workflows, Squad, Performance,
 * Medical, Admin. All other routes (Matches, Training, Analytics, Club,
 * Business, Guardrails) remain reachable by direct URL but are not present
 * in the nav.
 */

import { FeatureFlag } from "@/lib/subscriptionTiers";
import { ROUTES } from '@/lib/routes';

export type SectionKey =
  | "workflows"
  | "squad"
  | "performance"
  | "medical"
  | "admin";

export interface SectionDef {
  key: SectionKey;
  label: string;
  basePath: string;
  matchPaths: string[];
  feature?: FeatureFlag;
  roles?: string[];
}

export interface SectionPage {
  path: string;
  label: string;
  feature?: FeatureFlag;
  hideWhenLocked?: boolean;
  roles?: string[];
  action?: "open_performance_lab";
}

export const SECTIONS: SectionDef[] = [
  {
    key: "workflows",
    label: "Signals",
    basePath: ROUTES.WORKFLOWS,
    matchPaths: [
      ROUTES.WORKFLOWS,
      ROUTES.WORKFLOWS_ALL,
      ROUTES.WORKFLOWS_TEMPLATES,
      ROUTES.WORKFLOWS_NEW,
    ],
  },
  {
    key: "squad",
    label: "Squad",
    basePath: ROUTES.SQUAD,
    matchPaths: [
      ROUTES.SQUAD,
      ROUTES.PLAYERS,
      ROUTES.AVAILABILITY,
      ROUTES.COMPLIANCE,
      ROUTES.ACTIONS,
    ],
  },
  {
    key: "performance",
    label: "Performance",
    basePath: ROUTES.PERFORMANCE,
    matchPaths: [
      ROUTES.PERFORMANCE,
      ROUTES.GPS,
      ROUTES.LOAD_MONITORING,
      ROUTES.SPEED,
      ROUTES.FITNESS,
      ROUTES.BODYCOMP,
      ROUTES.METABOLIC,
      ROUTES.COGNITIVE,
      ROUTES.RISK_FORECAST,
      ROUTES.TESTING_CALENDAR,
      ROUTES.BLOCK_ANALYZER,
    ],
    roles: ["org_admin", "performance", "medical", "coach", "physio", "nutritionist", "analyst"],
  },
  {
    key: "medical",
    label: "Medical",
    basePath: ROUTES.WELLNESS,
    matchPaths: [
      ROUTES.WELLNESS,
      ROUTES.WELLNESS_SUMMARY,
      ROUTES.INJURIES,
      ROUTES.RTP,
    ],
    roles: ["org_admin", "performance", "medical", "coach", "physio"],
  },
  {
    key: "admin",
    label: "Admin",
    basePath: ROUTES.SETTINGS,
    matchPaths: [
      ROUTES.ADMIN,
      ROUTES.SETTINGS,
      ROUTES.USER_MANAGEMENT,
      ROUTES.DATA_MANAGEMENT,
      ROUTES.INTEGRATIONS,
      ROUTES.DATA_IMPORT,
      ROUTES.UPLOADS,
      ROUTES.UPLOAD_HISTORY,
      ROUTES.VALIDATION,
      ROUTES.CLUB_SETUP,
      ROUTES.SUPER_ADMIN,
      ROUTES.DIRECTOR_DASHBOARD,
    ],
    roles: ["org_admin", "super_admin", "performance", "medical", "coach"],
  },
];

export const SECTION_PAGES: Record<SectionKey, SectionPage[]> = {
  workflows: [
    { path: ROUTES.WORKFLOWS, label: "Dashboard" },
    { path: ROUTES.WORKFLOWS_ALL, label: "All Signals" },
    { path: ROUTES.WORKFLOWS_TEMPLATES, label: "Templates" },
  ],
  squad: [
    { path: ROUTES.PLAYERS, label: "Players" },
    { path: ROUTES.AVAILABILITY, label: "Availability", feature: "availability_tracking" },
    { path: ROUTES.COMPLIANCE, label: "Compliance" },
    { path: ROUTES.ACTIONS, label: "Actions" },
  ],
  performance: [
    { path: ROUTES.PERFORMANCE, label: "Assessments", feature: "gps_analytics" },
    { path: ROUTES.LOAD_MONITORING, label: "Load & Monitoring", feature: "gps_analytics" },
  ],
  medical: [
    { path: ROUTES.WELLNESS, label: "Wellness", feature: "wellness_tracking" },
    { path: ROUTES.INJURIES, label: "Injuries" },
    { path: ROUTES.RTP, label: "Return to Play", feature: "rtp_workflows" },
  ],
  admin: [
    { path: ROUTES.SETTINGS, label: "Settings" },
    { path: ROUTES.USER_MANAGEMENT, label: "Members" },
    { path: ROUTES.DATA_MANAGEMENT, label: "Data" },
    { path: ROUTES.INTEGRATIONS, label: "Integrations" },
    { path: ROUTES.UPLOADS, label: "Uploads", feature: "csv_uploads" },
    { path: ROUTES.DIRECTOR_DASHBOARD, label: "Director Dashboard", roles: ["org_admin"] },
  ],
};

/**
 * Find the section that owns the given pathname. Longest-prefix wins.
 */
export function findActiveSection(pathname: string): SectionDef | null {
  let best: { section: SectionDef; length: number } | null = null;
  for (const section of SECTIONS) {
    for (const p of section.matchPaths) {
      const isMatch = pathname === p || pathname.startsWith(p + "/");
      if (isMatch && (best === null || p.length > best.length)) {
        best = { section, length: p.length };
      }
    }
  }
  return best?.section ?? null;
}
